/* ============================================================================
 * TrackingDriver — public driver geolocation page.
 *
 * Mounts when the URL path matches /track/:token. The shipping admin
 * (or the shipper) generates a one-time token from a shipment and
 * sends the link via WhatsApp/SMS. The driver opens the link on their
 * phone, taps "Start tracking", grants geolocation permission, and the
 * browser pushes a position to Supabase every 60 s.
 *
 * Hard requirements:
 *  - No login. The driver is anonymous; the Supabase anon role must
 *    have RLS access to SELECT + UPDATE tracking_sessions BY TOKEN and
 *    INSERT into tracking_positions BY session_id.
 *  - The page is fully self-contained: it spins up its own
 *    @supabase/supabase-js client so it does NOT depend on the admin
 *    auth state held in src/supabaseClient.js.
 *  - Auto-stop when the driver is within ~2 km of the Pharr-Reynosa
 *    international bridge (lat 26.0649, lon -98.2300) — after that the
 *    crossing kills GPS reception anyway and the load is in customs'
 *    hands.
 *
 * Visual scale is mobile-first: large touch targets, big monospace
 * coordinates, high-contrast greens/reds, GFI navy header. Designed
 * to be legible on a low-end Android handset in direct sunlight.
 * ========================================================================= */

import { useEffect, useRef, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { LOGO_B64 } from "./SalesReports";

/* Pharr-Reynosa International Bridge crossing point. The driver's
   GPS stops broadcasting reliably at the bridge ramps, and once they
   queue for inspection the position no longer changes meaningfully —
   so we auto-stop within a 2 km radius. */
const BORDER_LAT = 26.1189;
const BORDER_LON = -98.1908;
const STOP_RADIUS_KM = 2;

/* Cadence between writes. The browser's watchPosition fires far more
   often than once a minute; the throttle keeps the positions table
   from ballooning while still giving Arturo near-realtime view. */
const SEND_INTERVAL_MS = 60 * 1000;

/* Bilingual strings — kept inline per the project convention. */
const T = {
  en: {
    brand: "Ground Fresh International — GPS Tracking",
    loading: "Loading…",
    inactive: "This link is no longer active.",
    inactiveSub: "Contact the GFI dispatcher if you need a new link.",
    notFound: "Tracking link not found.",
    lot: "Lot",
    driver: "Driver",
    shipper: "Shipper",
    start: "Start Tracking",
    stop: "Stop",
    starting: "Requesting GPS permission…",
    sharing: "Sharing location",
    lastPosition: "Last position",
    speed: "Speed",
    accuracy: "Accuracy",
    sentAt: "Last sent",
    finished: "You have arrived at the border. Tracking finished.",
    finishedSub: "Thank you — safe travels.",
    permissionDenied: "Location permission denied. Please enable it in your browser settings.",
    geoUnavailable: "GPS not available on this device.",
    networkErr: "Cannot reach the server. Will retry.",
    waiting: "Waiting for first GPS fix…",
    intro: "Tap below to start sharing your location with GFI.",
    note: "Your phone must keep this page open while driving.",
  },
  es: {
    brand: "Ground Fresh International — Rastreo GPS",
    loading: "Cargando…",
    inactive: "Este enlace ya no está activo.",
    inactiveSub: "Contacta al despachador de GFI si necesitas un enlace nuevo.",
    notFound: "Enlace de rastreo no encontrado.",
    lot: "Lote",
    driver: "Chofer",
    shipper: "Exportador",
    start: "Iniciar Rastreo",
    stop: "Detener",
    starting: "Solicitando permiso de GPS…",
    sharing: "Compartiendo ubicación",
    lastPosition: "Última posición",
    speed: "Velocidad",
    accuracy: "Precisión",
    sentAt: "Último envío",
    finished: "Has llegado a la frontera. Rastreo finalizado.",
    finishedSub: "Gracias — buen viaje.",
    permissionDenied: "Permiso de ubicación denegado. Habilítalo en la configuración del navegador.",
    geoUnavailable: "GPS no disponible en este dispositivo.",
    networkErr: "No se pudo conectar al servidor. Reintentando.",
    waiting: "Esperando primera lectura GPS…",
    intro: "Toca el botón para empezar a compartir tu ubicación con GFI.",
    note: "Mantén esta página abierta mientras conduces.",
  },
};

/* Lazy single anon-only Supabase client. The admin client in
   src/supabaseClient.js carries the staff session — we want a fresh
   one so the driver page doesn't accidentally pick up an admin
   token from the same browser. */
function makeAnonClient() {
  var url = process.env.REACT_APP_SUPABASE_URL;
  var key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  return createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
}

/* Haversine — km between two lat/lon pairs. Used for the border
   auto-stop radius check. */
function distanceKm(lat1, lon1, lat2, lon2) {
  var R = 6371;
  var toRad = function (d) { return d * Math.PI / 180; };
  var dLat = toRad(lat2 - lat1);
  var dLon = toRad(lon2 - lon1);
  var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2))
        * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/* Best-effort detection of "the driver speaks Spanish". Default to ES
   because every current GFI driver is Mexican. */
function detectLang() {
  try {
    var n = (navigator.language || navigator.userLanguage || "es").toLowerCase();
    return n.indexOf("es") === 0 ? "es" : "en";
  } catch (e) { return "es"; }
}

function fmtTime(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch (e) { return "—"; }
}

export default function TrackingDriver({ token }) {
  var [lang] = useState(detectLang);
  var t = T[lang];
  var [session, setSession] = useState(undefined); // undefined=loading, null=notfound, obj=loaded
  var [phase, setPhase] = useState("idle");        // idle | starting | active | finished | error
  var [errorMsg, setErrorMsg] = useState("");
  var [lastPos, setLastPos] = useState(null);
  var [lastSentAt, setLastSentAt] = useState(null);
  var supaRef = useRef(null);
  var watchIdRef = useRef(null);
  var lastSentMsRef = useRef(0);
  var stoppingRef = useRef(false); // guard against double auto-stop

  if (!supaRef.current) supaRef.current = makeAnonClient();
  var supa = supaRef.current;

  /* Initial fetch of the session row. We don't auto-flip the row to
     "active" here — that only happens after the driver taps Start, so
     a curious user opening the link doesn't burn the token. */
  useEffect(function () {
    if (!token) { setSession(null); return; }
    var alive = true;
    supa.from("tracking_sessions").select("*").eq("token", token).maybeSingle()
      .then(function (res) {
        if (!alive) return;
        if (res.error || !res.data) { setSession(null); return; }
        setSession(res.data);
        if (res.data.status === "stopped") setPhase("finished");
      })
      .catch(function () { if (alive) setSession(null); });
    return function () { alive = false; };
  }, [token, supa]);

  /* Stop the watch + flip the row. Called by the manual Stop button
     and the auto-border guard. Idempotent so accidental double-tap
     doesn't double-write. */
  var stopTracking = function (reason) {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    if (watchIdRef.current != null && navigator.geolocation) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (e) {}
      watchIdRef.current = null;
    }
    var newPhase = reason === "border" ? "finished" : "idle";
    setPhase(newPhase);
    if (session && session.id) {
      supa.from("tracking_sessions")
        .update({ status: "stopped", stopped_at: new Date().toISOString(), stop_reason: reason || "manual" })
        .eq("id", session.id)
        .then(function () { /* swallow — UI already updated */ });
    }
  };

  /* watchPosition success handler. Throttles to SEND_INTERVAL_MS so
     the table doesn't fill up with sub-second pings. */
  var onPosition = function (pos) {
    var lat = pos.coords.latitude;
    var lon = pos.coords.longitude;
    var sp  = pos.coords.speed;        // m/s, can be null on some browsers
    var acc = pos.coords.accuracy;
    var ts  = pos.timestamp ? new Date(pos.timestamp).toISOString() : new Date().toISOString();
    setLastPos({ lat: lat, lon: lon, speed: sp, accuracy: acc, ts: ts });

    /* Border auto-stop — check on every fix regardless of throttle. */
    var d = distanceKm(lat, lon, BORDER_LAT, BORDER_LON);
    if (d <= STOP_RADIUS_KM) { stopTracking("border"); return; }

    var now = Date.now();
    if (now - lastSentMsRef.current < SEND_INTERVAL_MS) return;
    lastSentMsRef.current = now;

    if (!session || !session.id) return;
    supa.from("tracking_positions").insert([{
      session_id: session.id,
      lat: lat,
      lon: lon,
      speed_mps: (sp == null ? null : sp),
      accuracy_m: (acc == null ? null : acc),
      recorded_at: ts,
    }]).then(function (res) {
      if (res.error) { setErrorMsg(t.networkErr); return; }
      setErrorMsg("");
      setLastSentAt(new Date());
    }).catch(function () { setErrorMsg(t.networkErr); });
  };

  var onPositionError = function (err) {
    if (err && err.code === 1) { setPhase("error"); setErrorMsg(t.permissionDenied); }
    else if (err && err.code === 2) { setErrorMsg(t.geoUnavailable); }
    else { setErrorMsg(t.networkErr); }
  };

  /* Driver tapped the big green button. Flip the row to "active",
     ask the browser for GPS permission, install the watcher. */
  var startTracking = function () {
    if (!session || !session.id) return;
    if (!navigator.geolocation) { setPhase("error"); setErrorMsg(t.geoUnavailable); return; }
    setPhase("starting");
    /* Mark active server-side first so the admin map can light up
       even before the first position arrives. */
    supa.from("tracking_sessions")
      .update({ status: "active", started_at: session.started_at || new Date().toISOString() })
      .eq("id", session.id)
      .then(function () { /* fire and forget */ });

    try {
      watchIdRef.current = navigator.geolocation.watchPosition(
        onPosition,
        onPositionError,
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 }
      );
      setPhase("active");
    } catch (e) {
      setPhase("error"); setErrorMsg(t.geoUnavailable);
    }
  };

  /* On unmount, clear the watch — but do NOT auto-stop the session.
     The driver may have hit Back accidentally; the cron-style admin
     view can still pick the session back up if they reopen the link. */
  useEffect(function () {
    return function () {
      if (watchIdRef.current != null && navigator.geolocation) {
        try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (e) {}
      }
    };
  }, []);

  /* ── Render branches ─────────────────────────────────────────── */

  var pageBg = "linear-gradient(180deg, #0D1B2A 0%, #152A40 100%)";
  var cardStyle = {
    background: "#fff",
    color: "#1B3A4B",
    borderRadius: 16,
    padding: "20px 18px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
    width: "100%",
    maxWidth: 420,
    boxSizing: "border-box",
  };
  var rowKv = { display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "6px 0", borderBottom: "1px solid #E5EAF0" };
  var kvK = { fontSize: 11, fontWeight: 700, color: "#6A7A90", textTransform: "uppercase", letterSpacing: "0.05em" };
  var kvV = { fontSize: 15, fontWeight: 600, color: "#1B3A4B", fontFamily: "'JetBrains Mono', monospace" };

  var Shell = function (props) {
    return (
      <div style={{
        minHeight: "100vh", width: "100%",
        background: pageBg,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
        padding: "24px 16px 32px",
        boxSizing: "border-box",
        fontFamily: "'DM Sans', sans-serif",
      }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 18 }}>
          <img src={LOGO_B64} alt="GFI" style={{ width: 64, height: 64, borderRadius: 12, background: "#fff", padding: 6, boxSizing: "border-box" }} />
          <div style={{ marginTop: 10, color: "#fff", fontSize: 14, fontWeight: 700, textAlign: "center", letterSpacing: "0.02em" }}>{t.brand}</div>
        </div>
        {props.children}
      </div>
    );
  };

  if (session === undefined) {
    return <Shell><div style={cardStyle}><div style={{ textAlign: "center", fontSize: 15, color: "#6A7A90" }}>{t.loading}</div></div></Shell>;
  }
  if (session === null) {
    return (
      <Shell>
        <div style={cardStyle}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#DC2626", marginBottom: 6 }}>{t.notFound}</div>
          <div style={{ fontSize: 13, color: "#6A7A90" }}>{t.inactiveSub}</div>
        </div>
      </Shell>
    );
  }
  /* Loaded but explicitly stopped on the server → show terminal state. */
  if (session.status === "stopped" && phase !== "finished") {
    return (
      <Shell>
        <div style={cardStyle}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#DC2626", marginBottom: 6 }}>{t.inactive}</div>
          <div style={{ fontSize: 13, color: "#6A7A90" }}>{t.inactiveSub}</div>
        </div>
      </Shell>
    );
  }
  if (phase === "finished") {
    return (
      <Shell>
        <div style={cardStyle}>
          <div style={{ fontSize: 22, lineHeight: 1.2, fontWeight: 800, color: "#0F766E", marginBottom: 6 }}>✓ {t.finished}</div>
          <div style={{ fontSize: 14, color: "#6A7A90" }}>{t.finishedSub}</div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div style={cardStyle}>
        <div style={rowKv}><span style={kvK}>{t.lot}</span><span style={kvV}>{session.lot || "—"}</span></div>
        <div style={rowKv}><span style={kvK}>{t.driver}</span><span style={kvV}>{session.driver_name || "—"}</span></div>
        <div style={{ ...rowKv, borderBottom: "none" }}><span style={kvK}>{t.shipper}</span><span style={kvV}>{session.shipper_name || "—"}</span></div>
      </div>

      {phase !== "active" && phase !== "starting" && (
        <div style={{ width: "100%", maxWidth: 420, marginTop: 18 }}>
          <div style={{ color: "#fff", fontSize: 14, lineHeight: 1.4, textAlign: "center", marginBottom: 14, opacity: 0.9 }}>{t.intro}</div>
          <button
            onClick={startTracking}
            style={{
              width: "100%",
              padding: "18px 16px",
              borderRadius: 12,
              border: "none",
              background: "#10B981",
              color: "#fff",
              fontSize: 20,
              fontWeight: 800,
              letterSpacing: "0.02em",
              boxShadow: "0 6px 16px rgba(16,185,129,0.35)",
              cursor: "pointer",
            }}
          >▶ {t.start}</button>
          <div style={{ color: "#9FB3CA", fontSize: 12, textAlign: "center", marginTop: 12, opacity: 0.9 }}>{t.note}</div>
          {errorMsg && phase === "error" && (
            <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.18)", color: "#FECACA", fontSize: 13, fontWeight: 600, textAlign: "center" }}>{errorMsg}</div>
          )}
        </div>
      )}

      {phase === "starting" && (
        <div style={{ marginTop: 18, color: "#fff", fontSize: 14, textAlign: "center" }}>{t.starting}</div>
      )}

      {phase === "active" && (
        <>
          <div style={{ width: "100%", maxWidth: 420, marginTop: 18 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "12px 14px", borderRadius: 10,
              background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.45)",
            }}>
              <span style={{
                display: "inline-block", width: 12, height: 12, borderRadius: 6,
                background: "#10B981", boxShadow: "0 0 0 0 rgba(16,185,129,0.7)",
                animation: "gfiPulse 1.4s infinite",
              }} />
              <span style={{ color: "#D1FAE5", fontSize: 14, fontWeight: 700 }}>{t.sharing}…</span>
            </div>
          </div>

          <div style={{ ...cardStyle, marginTop: 14 }}>
            {lastPos ? (
              <>
                <div style={rowKv}>
                  <span style={kvK}>{t.lastPosition}</span>
                  <span style={kvV}>{lastPos.lat.toFixed(5)}, {lastPos.lon.toFixed(5)}</span>
                </div>
                <div style={rowKv}>
                  <span style={kvK}>{t.speed}</span>
                  <span style={kvV}>{lastPos.speed != null ? (lastPos.speed * 3.6).toFixed(0) + " km/h" : "—"}</span>
                </div>
                <div style={rowKv}>
                  <span style={kvK}>{t.accuracy}</span>
                  <span style={kvV}>{lastPos.accuracy != null ? "±" + Math.round(lastPos.accuracy) + " m" : "—"}</span>
                </div>
                <div style={{ ...rowKv, borderBottom: "none" }}>
                  <span style={kvK}>{t.sentAt}</span>
                  <span style={kvV}>{fmtTime(lastSentAt)}</span>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", color: "#6A7A90", fontSize: 14, padding: "8px 0" }}>{t.waiting}</div>
            )}
            {errorMsg && (
              <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(220,38,38,0.1)", color: "#B91C1C", fontSize: 12, fontWeight: 600, textAlign: "center" }}>{errorMsg}</div>
            )}
          </div>

          <div style={{ width: "100%", maxWidth: 420, marginTop: 18 }}>
            <button
              onClick={function () { stopTracking("manual"); }}
              style={{
                width: "100%",
                padding: "14px 16px",
                borderRadius: 10,
                border: "none",
                background: "#DC2626",
                color: "#fff",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >■ {t.stop}</button>
          </div>
        </>
      )}

      {/* Pulse keyframes — inlined so this component has zero CSS dep. */}
      <style>{"@keyframes gfiPulse{0%{box-shadow:0 0 0 0 rgba(16,185,129,.7)}70%{box-shadow:0 0 0 14px rgba(16,185,129,0)}100%{box-shadow:0 0 0 0 rgba(16,185,129,0)}}"}</style>
    </Shell>
  );
}
